import { toEnglishDigits } from "./toEnglishDigits";
import { getDepartureRules } from "./departureRulesCache";
import {
  formatDate,
  formatTime,
  formatDateTime,
  formatDateBilingual,
  formatFull,
  parseDate,
  toLatinDigits,
  AR_DAYS,
  EN_MONTHS,
} from "./dateTime";

// All dates/times output Latin 0-9 in 24-hour format.
// fmtDate / fmtTime / fmtDateTime are LEGACY aliases. Prefer formatDate /
// formatTime / formatDateTime from "./dateTime" for new code.

export function fmtDate(iso) {
  if (!iso) return "—";
  const s = formatDate(iso);
  return s || toEnglishDigits(iso).slice(0, 10) || "—";
}

export function fmtTime(iso) {
  if (!iso && iso !== 0) return "—";
  const s = formatTime(iso);
  return s || "—";
}

export function fmtDateTime(iso) {
  return `${fmtDate(iso)} · ${fmtTime(iso)}`;
}

// combine YYYY-MM-DD + HH:mm → ISO
export function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = (timeStr || "00:00").slice(0, 5);
  return new Date(`${dateStr}T${t}:00`).toISOString();
}

// Local-time YYYY-MM-DD (never UTC). Safe for timezone-sensitive filters —
// avoid `.toISOString().slice(0,10)` which off-by-ones in UTC+3 (KSA/UAE).
export function toLocalDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Local "today" with day offset (e.g., -1 = yesterday, +1 = tomorrow).
export function localDateOffset(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return toLocalDateStr(d);
}

// Arabic weekday for a YYYY-MM-DD (or any parseable date string). Interpreted
// in the LOCAL timezone so it matches what the user reads in the calendar.
const AR_WEEKDAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
export function arabicDayOfWeek(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();
  // Force local midnight when only YYYY-MM-DD is provided to avoid UTC drift.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return AR_WEEKDAYS[d.getDay()] || "";
}

// ─── Departure time engine ────────────────────────────────────────────
// Ordered list. First matching rule wins. `pickup: null` = matches any origin.
// Timing rules provided by ops (see task 8-rule spec). Offsets in MINUTES.
export const DEPARTURE_RULES = [
  // Train stations first (short 90-min buffer)
  { key: "makkah_to_train",   pickup: /مك[ةه]/i,        dest: /(قطار|محطة).*(مك[ةه])|(مك[ةه]).*(قطار|محطة)/i,           offsetMin: 90,  label: "قبل موعد القطار بـ ساعة ونصف" },
  { key: "madinah_to_train",  pickup: /المدين[ةه]/i,    dest: /(قطار|محطة).*(المدين[ةه])|(المدين[ةه]).*(قطار|محطة)/i,   offsetMin: 90,  label: "قبل موعد القطار بـ ساعة ونصف" },
  // Cross-city airport transfers (long buffer)
  { key: "madinah_to_jed_ap", pickup: /المدين[ةه]/i,    dest: /جدة.*مطار|مطار.*جدة/i,                                    offsetMin: 450, label: "قبل الإقلاع بـ 7 ساعات ونصف" },
  { key: "makkah_to_jed_ap",  pickup: /مك[ةه]/i,        dest: /جدة.*مطار|مطار.*جدة/i,                                    offsetMin: 240, label: "قبل الإقلاع بـ 4 ساعات" },
  { key: "makkah_to_taif_ap", pickup: /مك[ةه]/i,        dest: /(الطائف|طائف).*مطار|مطار.*(الطائف|طائف)/i,               offsetMin: 240, label: "قبل الإقلاع بـ 4 ساعات" },
  // Same-city airport transfers
  { key: "jed_to_jed_ap",     pickup: /جدة/i,           dest: /جدة.*مطار|مطار.*جدة/i,                                    offsetMin: 180, label: "قبل الإقلاع بـ 3 ساعات" },
  { key: "mad_to_mad_ap",     pickup: /المدين[ةه]/i,    dest: /(المدين[ةه]).*مطار|مطار.*(المدين[ةه])/i,                 offsetMin: 180, label: "قبل الإقلاع بـ 3 ساعات" },
  { key: "taif_to_taif_ap",   pickup: /الطائف|طائف/i,   dest: /(الطائف|طائف).*مطار|مطار.*(الطائف|طائف)/i,               offsetMin: 180, label: "قبل الإقلاع بـ 3 ساعات" },
  // Generic airport fallback
  { key: "any_to_airport",    pickup: null,             dest: /مطار|airport/i,                                            offsetMin: 240, label: "قبل الإقلاع بـ 4 ساعات" },
];

// Compute departure time for pickup/dest/tripTime, honouring an explicit override.
// Returns { time: "HH:MM", offsetMin, label, isOverride } | null.
//
// Rule source priority:
//   1. Admin-managed rules from the API (via `departureRulesCache`).
//      • Smart matching: substring / normalised containment on pickup & dest.
//      • When multiple rules match, the most specific one wins (longest
//        combined pickup+destination length).
//   2. Hard-coded fallback in DEPARTURE_RULES (regex-based, legacy).
export function computeDepartureRule(pickup, dest, tripTime, override) {
  const ov = (override || "").trim();
  if (ov) {
    return { time: ov, offsetMin: 0, label: "تجاوز يدوي من الإدارة", isOverride: true };
  }
  if (!tripTime || !dest) return null;
  const p = String(pickup || "");
  const d = String(dest);

  // Compute the offset time given an offsetMin — helper used by both branches.
  const applyOffset = (offsetMin, label, key) => {
    const m = String(tripTime).match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const H = parseInt(m[1], 10);
    const M = parseInt(m[2], 10);
    let total = H * 60 + M - offsetMin;
    total = ((total % 1440) + 1440) % 1440;
    const hh = String(Math.floor(total / 60)).padStart(2, "0");
    const mm = String(total % 60).padStart(2, "0");
    return { time: `${hh}:${mm}`, offsetMin, label, isOverride: false, key };
  };

  // ── 1) Admin-managed rules from the API cache ──────────────────────
  const normalize = (s) => String(s || "").toLowerCase().replace(/\s+/g, "").trim();
  const dynamicRules = getDepartureRules();
  if (dynamicRules && dynamicRules.length > 0) {
    const pn = normalize(p);
    const dn = normalize(d);
    // Longest-first so more specific rules win.
    const sorted = [...dynamicRules].sort(
      (a, b) => (String(b.destination).length + String(b.pickup_location).length)
              - (String(a.destination).length + String(a.pickup_location).length),
    );
    for (const r of sorted) {
      const rp = normalize(r.pickup_location);
      const rd = normalize(r.destination);
      if (!rp || !rd) continue;
      // Two-way containment: either rule string is inside booking string, or vice-versa.
      const puOk = pn.includes(rp) || rp.includes(pn);
      const dnOk = dn.includes(rd) || rd.includes(dn);
      if (puOk && dnOk) {
        const label = r.note ? `قاعدة إدارية · ${r.note}` : `قاعدة إدارية (${r.pickup_location} → ${r.destination})`;
        return applyOffset(r.offset_minutes, label, `rule:${r.id}`);
      }
    }
  }

  // ── 2) Hard-coded fallback ─────────────────────────────────────────
  const rule = DEPARTURE_RULES.find((r) => (!r.pickup || r.pickup.test(p)) && r.dest.test(d));
  if (!rule) return null;
  return applyOffset(rule.offsetMin, rule.label, rule.key);
}

// Re-export new API so callers can `import { formatDate } from "@/utils/date"`
export {
  formatDate,
  formatTime,
  formatDateTime,
  formatDateBilingual,
  formatFull,
  parseDate,
  toLatinDigits,
  AR_DAYS,
  EN_MONTHS,
};

// Chip ranges: today / yesterday / last 7 days / last 30 days
export function chipRange(kind) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = (offset) => {
    const d = new Date(today);
    d.setDate(d.getDate() + offset);
    return d;
  };
  const toIso = (d) => new Date(d).toISOString();
  switch (kind) {
    case "yesterday": {
      const s = day(-1);
      const e = day(0);
      e.setMilliseconds(-1);
      return { from: toIso(s), to: toIso(e) };
    }
    case "today": {
      const s = day(0);
      const e = day(1);
      e.setMilliseconds(-1);
      return { from: toIso(s), to: toIso(e) };
    }
    case "tomorrow": {
      const s = day(1);
      const e = day(2);
      e.setMilliseconds(-1);
      return { from: toIso(s), to: toIso(e) };
    }
    case "last7": {
      const s = day(-7);
      const e = day(1);
      e.setMilliseconds(-1);
      return { from: toIso(s), to: toIso(e) };
    }
    case "last30": {
      const s = day(-30);
      const e = day(1);
      e.setMilliseconds(-1);
      return { from: toIso(s), to: toIso(e) };
    }
    default:
      return { from: null, to: null };
  }
}
